/**
 * TaskBulkActions Component
 *
 * Floating action bar for bulk task operations.
 * Accepts team members via props instead of using mock data.
 */

import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip,
  Transition,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCheck,
  IconChevronDown,
  IconPencil,
  IconTrash,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import type { TaskInboxStatus, TaskPriority } from '../types';
import { TASK_PRIORITY_OPTIONS, TASK_STATUS_FILTER_OPTIONS, TaskInboxStatusLabels, TaskPriorityLabels } from '../types';
import type { TeamMember } from '../hooks/useTeamMembers';

export interface BulkEditChanges {
  status?: TaskInboxStatus;
  priority?: TaskPriority;
  assignee?: string;
}

interface TaskBulkActionsProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkApplyChanges: (changes: BulkEditChanges) => void;
  onBulkDelete: () => void;
  teamMembers: TeamMember[];
}

const bulkStatusOptions: { value: string; label: string }[] = [
  { value: '', label: 'No change' },
  ...TASK_STATUS_FILTER_OPTIONS,
];

const bulkPriorityOptions: { value: string; label: string }[] = [
  { value: '', label: 'No change' },
  ...TASK_PRIORITY_OPTIONS,
];

export function TaskBulkActions({
  selectedCount,
  onClearSelection,
  onBulkApplyChanges,
  onBulkDelete,
  teamMembers,
}: TaskBulkActionsProps): JSX.Element {
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [status, setStatus] = useState<string>('');
  const [priority, setPriority] = useState<string>('');
  const [assignee, setAssignee] = useState<string>('');

  const assigneeOptions = [
    { value: '', label: 'No change' },
    ...teamMembers.map((m) => ({ value: m.reference, label: m.name })),
  ];

  const handleOpenEdit = useCallback(() => {
    setStatus('');
    setPriority('');
    setAssignee('');
    openEditModal();
  }, [openEditModal]);

  const handleConfirmEdit = useCallback(() => {
    const changes: BulkEditChanges = {};
    if (status) changes.status = status as TaskInboxStatus;
    if (priority) changes.priority = priority as TaskPriority;
    if (assignee) changes.assignee = assignee;
    if (Object.keys(changes).length === 0) {
      closeEditModal();
      return;
    }
    onBulkApplyChanges(changes);
    closeEditModal();
  }, [status, priority, assignee, onBulkApplyChanges, closeEditModal]);

  const hasAnyChange = Boolean(status || priority || assignee);

  const summaryLines: string[] = [];
  if (status) summaryLines.push(`Status → ${TaskInboxStatusLabels[status as TaskInboxStatus]}`);
  if (priority) summaryLines.push(`Priority → ${TaskPriorityLabels[priority as TaskPriority]}`);
  if (assignee) {
    const name = teamMembers.find((m) => m.reference === assignee)?.name ?? assignee;
    summaryLines.push(`Assign to → ${name}`);
  }

  return (
    <>
      <Transition mounted={selectedCount > 0} transition="slide-up" duration={200}>
        {(styles) => (
          <Paper
            style={{
              ...styles,
              position: 'fixed',
              bottom: 'var(--mantine-spacing-lg)',
              left: '50%',
              transform: styles.transform
                ? `translateX(-50%) ${String(styles.transform)}`
                : 'translateX(-50%)',
              zIndex: 100,
            }}
            shadow="lg"
            radius="md"
            p="sm"
            withBorder
          >
            <Group gap="md" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Badge size="lg" variant="filled">
                  {selectedCount}
                </Badge>
                <Text size="sm" fw={500}>
                  Task{selectedCount !== 1 ? 's' : ''} Selected
                </Text>
                <Tooltip label="Clear selection">
                  <ActionIcon variant="subtle" size="sm" onClick={onClearSelection} aria-label="Clear selection">
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Divider orientation="vertical" />

              <Tooltip label="Mark selected as complete">
                <Button
                  variant="subtle"
                  size="xs"
                  color="green"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => onBulkApplyChanges({ status: 'completed' })}
                >
                  Mark complete
                </Button>
              </Tooltip>

              <Divider orientation="vertical" />

              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                rightSection={<IconChevronDown size={14} />}
                onClick={handleOpenEdit}
              >
                Edit selected
              </Button>

              <Divider orientation="vertical" />

              <Tooltip label="Delete selected tasks">
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={onBulkDelete}
                >
                  Delete
                </Button>
              </Tooltip>
            </Group>
          </Paper>
        )}
      </Transition>

      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title="Bulk edit tasks"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Apply the following changes to {selectedCount} selected task(s). Leave a field empty to
            keep current value.
          </Text>

          <Select
            label="Status"
            placeholder="No change"
            data={bulkStatusOptions}
            value={status}
            onChange={(v) => setStatus(v ?? '')}
            clearable
          />

          <Select
            label="Priority"
            placeholder="No change"
            data={bulkPriorityOptions}
            value={priority}
            onChange={(v) => setPriority(v ?? '')}
            clearable
          />

          <Select
            label="Assign to"
            placeholder="No change"
            leftSection={<IconUser size={16} />}
            data={assigneeOptions}
            value={assignee}
            onChange={(v) => setAssignee(v ?? '')}
            clearable
          />

          {hasAnyChange && (
            <Paper p="sm" withBorder bg="gray.0" radius="md">
              <Text size="xs" c="dimmed" mb={4}>
                Summary of changes
              </Text>
              <Stack gap={2}>
                {summaryLines.map((line) => (
                  <Text key={line} size="sm">
                    {line}
                  </Text>
                ))}
              </Stack>
            </Paper>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button
              disabled={!hasAnyChange}
              onClick={handleConfirmEdit}
            >
              Apply to {selectedCount} task(s)
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
